import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import i18n from '../../../assets/languages/configs';
import BuyComboItem from '../../../components/BuyComboItem/BuyComboItem';
import SelectNumber from '../../../components/SelectNumber/SelectNumber';
import EmptyData from '../../../components/empty-data/EmptyData';
import Header from '../../../components/header/Header';
import CenterLoading from '../../../components/CenterLoading/CenterLoading';
import TextBreaks from '../../../components/TextBreaks/TextBreaks';
import CustomAntButton from '../../../components/CustomAntButton/CustomAntButton';

import useTicketInfo from '../../../hooks/useTicketInfo';
import { useReservationFlow } from '../../../hooks/useReservationFlow';

import { localStorageKeys, VOUCHER_TYPE } from '../../../utils/Constants';
import { LocalStorageManager } from '../../../utils/LocalStorageManager/LocalStorageManager';
import {
    convertTextToLinks,
    getValueByLanguage,
    hasSelectedNumber,
    isEmptyObject,
    isOnSaleTicket,
    isReserveTicket,
    showNotificationDialog,
} from '../../../utils';

import styles from './styles.module.css';
import { MultilingualType, SelectNumber as SelectNumberT } from 'types';
/**
 * チケット購入画面
 */
const ReservationTicketInfoScreen = () => {
    const [selectNumber, setSelectNumber] = useState<SelectNumberT | null>({});
    const [customDefaultNumber, setCustomDefaultNumber] = useState<SelectNumberT | null>(null);
    const [searchParams] = useSearchParams();
    const { t } = useTranslation();
    const { isLoading: isFetchingTicketInfo, ticketInfo, fetchTicketInfo } = useTicketInfo();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { back, reservationCombo, isEdit, localReservation } = useReservationFlow();

    const { language } = i18n;
    const salesTicketId = searchParams.get('salesTicketId');
    const l = useCallback((text?: MultilingualType | null) => getValueByLanguage(text, language), [language]);

    const localStorageManager = useMemo(() => new LocalStorageManager('ReservationTicketInfoScreen'), []);
    const voucherCode = useMemo(() => searchParams.get('voucherCode'), [searchParams]);

    // 初回レンダリング
    useEffect(() => {
        //TODO 人数変更できないためisEdit: falseにする
        localStorage.setItem(
            localStorageKeys.RESERVATION_INFO_DATA,
            JSON.stringify({ ...localReservation, isEdit: false }),
        );
        const localTicketInfo = localStorageManager.getStorage(localStorageKeys.TICKET_INFO_DATA);
        let salesTicketId;
        if (localTicketInfo?.salesTicketId) {
            salesTicketId = localTicketInfo.salesTicketId ?? null;
            setCustomDefaultNumber(localTicketInfo.selectNumber);
        }

        // get parameterのticketIdを優先する。
        if (searchParams.get('salesTicketId')) {
            salesTicketId = searchParams.get('salesTicketId');
            if (String(localTicketInfo?.salesTicketId) !== String(salesTicketId)) {
                setCustomDefaultNumber(null);
                localStorage.removeItem(localStorageKeys.TICKET_INFO_DATA);
                localStorage.removeItem(localStorageKeys.RESERVATION_INFO_DATA);
                localStorage.removeItem(localStorageKeys.ANONYMOUS_FORM_ANSWERS);
                localStorage.removeItem(localStorageKeys.RESERVATION_FLOW);
            }
        }
        if (!salesTicketId) {
            navigate('/buy');
            return;
        } else if (salesTicketId) {
            void fetchTicketInfo(salesTicketId);
        }
    }, []);

    useEffect(() => {
        if (ticketInfo && !isEmptyObject(ticketInfo) && !isReserveTicket(ticketInfo)) {
            const path = voucherCode
                ? `/buy/ticket?salesTicketId=${ticketInfo?.salesTicketId}&voucherCode=${voucherCode}`
                : `/buy/ticket?salesTicketId=${ticketInfo?.salesTicketId}`;
            navigate(path, { replace: true });
        }
    }, [ticketInfo, voucherCode]);

    const totalSelectNumber = useMemo(() => {
        if (!selectNumber) {
            return 0;
        }
        return Object.values(selectNumber)
            .filter((value) => value !== undefined && value !== null)
            .reduce((accumulator, currentValue) => accumulator + currentValue, 0);
    }, [selectNumber]);

    // 更新ボタン押下時
    const handleRefetch = () => {
        if (!salesTicketId) return;
        void fetchTicketInfo(salesTicketId);
    };

    const handleChangeNumber = useCallback(
        (number: SelectNumberT | null) => {
            const localTicketInfo = localStorageManager.getStorage(localStorageKeys.TICKET_INFO_DATA) ?? {};
            localStorageManager.setStorage(localStorageKeys.TICKET_INFO_DATA, {
                ...localTicketInfo,
                selectNumber: number,
            });
            setSelectNumber(number);
        },
        [localStorageManager],
    );

    if (!ticketInfo) {
        return <CenterLoading />;
    }

    const { isDisplayRemainingCount, maxParticipantCount, currentParticipantCount } = ticketInfo;

    const hasParticipantCount = maxParticipantCount != null;
    const isDisplayRemaining = isDisplayRemainingCount && hasParticipantCount;
    const remainingCount = hasParticipantCount ? maxParticipantCount - (currentParticipantCount ?? 0) : -1;

    const handleNextStep = () => {
        if (!hasSelectedNumber(selectNumber)) {
            showNotificationDialog(
                'error',
                'selectNumberError',
                t('notification.title'),
                t('validate.minimum-one-person'),
            );
            return;
        }

        const canReservationTickets = ticketInfo?.comboItems
            ?.filter((item) => item.isReserve)
            ?.map((item) => {
                const reservationTicket = reservationCombo?.find((r) => r.displayPriority === item.displayPriority);
                return reservationTicket ? { ...reservationTicket, ...item } : item;
            });

        localStorage.setItem(
            localStorageKeys.RESERVATION_INFO_DATA,
            JSON.stringify({
                ...localReservation,
                ticketInfo: ticketInfo,
                selectNumber: selectNumber,
                reservationCombo: canReservationTickets,
            }),
        );
        const hasVoucherSupport =
            ticketInfo?.voucherType === VOUCHER_TYPE.FIXED_FIRST ||
            ticketInfo?.voucherType === VOUCHER_TYPE.ONE_TIME_FIRST;
        const shouldRedirectToVoucherScreen = ticketInfo?.isDisplayAnonymousForm === true || hasVoucherSupport;
        let path = '';
        if (shouldRedirectToVoucherScreen) {
            path = hasVoucherSupport && voucherCode ? `/buy/vouchers?voucherCode=${voucherCode}` : '/buy/vouchers';
        } else {
            path = 'calendar';
        }

        dispatch({
            type: 'RESERVATION_FLOW',
            payload: {
                currentIndex: 0,
            },
        });
        navigate(path);
    };

    return (
        <>
            <Header
                title={!isEdit ? t('header.buy-ticket') : t('reservation.header')}
                refetch={handleRefetch}
                callback={back}
            />
            {isFetchingTicketInfo && <CenterLoading />}
            <div className="content-container scrollable">
                {navigator.onLine && !isEmptyObject(ticketInfo) ? (
                    <div className={styles.ticketInfoWrapper}>
                        {/* チケットの情報 */}
                        <div className={styles.ticketInfo}>
                            <h4 className={styles.ticketName}>
                                <TextBreaks text={l(ticketInfo?.ticketName)} />
                            </h4>
                            <p className={styles.descriptions}>
                                <TextBreaks text={l(ticketInfo?.subtitle)} />
                            </p>
                            {isDisplayRemaining && remainingCount >= 0 && isOnSaleTicket(ticketInfo) && (
                                <div className={styles.descriptions}>
                                    {`${t('reservation.residue')}: ${remainingCount}`}
                                </div>
                            )}
                        </div>
                        {ticketInfo.isCombination && (
                            <BuyComboItem
                                ticketInfo={ticketInfo}
                                comboItem={ticketInfo.comboItems}
                                selectNumber={selectNumber}
                            />
                        )}
                        {/* <div className={styles.reservationDescription}>{t('reservation.required')}</div> */}
                        {/* 人数を選択 */}
                        <div className={styles.personPicker}>
                            <SelectNumber
                                isReserve={ticketInfo.isReserve}
                                ticketInfo={ticketInfo}
                                onChange={handleChangeNumber}
                                customDefaultNumber={customDefaultNumber}
                            />
                        </div>

                        {/* 注意項目 */}
                        {ticketInfo?.note && (
                            <p className={styles.textNote}>{convertTextToLinks(l(ticketInfo.note))}</p>
                        )}
                        {/* 次へ進むボタン */}
                        <div className={styles.btnArea}>
                            {isOnSaleTicket(ticketInfo) &&
                            (remainingCount === -1 || remainingCount >= totalSelectNumber) ? (
                                <CustomAntButton
                                    type="outlined"
                                    className={styles.coloredBtn}
                                    block
                                    onClick={handleNextStep}
                                >
                                    {t('reservation.next-step')}
                                </CustomAntButton>
                            ) : (
                                <CustomAntButton type="disabled" className={styles.disableBtn} block disabled>
                                    {t(
                                        hasParticipantCount &&
                                            remainingCount <= totalSelectNumber &&
                                            isOnSaleTicket(ticketInfo)
                                            ? 'reservation.sold-out'
                                            : 'button.not-on-sale',
                                    )}
                                </CustomAntButton>
                            )}
                        </div>
                    </div>
                ) : (
                    !isFetchingTicketInfo && <EmptyData />
                )}
            </div>
        </>
    );
};

export default memo(ReservationTicketInfoScreen);
